import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormHelperText,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  Clear,
  Close,
  Edit,
  Lock,
  PictureAsPdf,
  Visibility,
} from '@mui/icons-material';
import dayjs from 'dayjs';
import {
  useApiGet,
  useApiSend,
  getApiErrorMessage,
} from '../../utils/httpClientTs';
import {
  getFundStatementDates,
  getFundStatementData,
  postFundStatementOverride,
  getFundStatementPdfBlob,
  downloadFundStatementPdf,
  lockFundStatementPdf,
  saveFundStatementPrincipalRetained,
  recalculateFundStatementDistributions,
} from '../../endpoints/financeFunds';
import type {
  FundStatementDataResponse,
  FundStatementDatesResponse,
  FundStatementFieldDTO,
  FundYearlyTransferAllocationDTO,
  PostFundStatementOverrideRequest,
} from '../../types/financeFunds';
import {
  FUND_CATEGORY_TYPE_ALLOCATION_ID,
  fundSubtypeIsSpecialRecognition,
  fundSubtypeRequiresNamedLimit,
  fundTypeStatementDisplayName,
  distributionToStatementLabel,
} from '../../types/financeFunds';
import AlertItem from '../../Components/AlertItem';

interface FundStatementDataTabProps {
  fundId: string;
  canManage: boolean;
}

const PRINCIPAL_RETAINED_FIELD = 'principal_retained';
const INCOME_TRANSFER_FIELD = 'income_transfer';
const FUND_CATEGORY_SCHOLARSHIP_ID = '58de4a1f0c1045a8a1de9e56335f17e2';
const FUND_CATEGORY_CHAPTER_DONOR_DESIGNATED_ID =
  'cf2f827f28554b3ba702bebdf48a9fa0';

const formatMoney = (
  amount: number | null | undefined,
  opts?: { subtract?: boolean }
) => {
  if (amount == null || !Number.isFinite(amount)) return '—';
  const subtract = Boolean(opts?.subtract) || amount < 0;
  const abs = Math.abs(amount);
  const body = abs.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  if (subtract) return `(${body})`;
  return `$${body}`;
};

const showMipActivityRow = (field: FundStatementFieldDTO) => {
  if (field.field_name === PRINCIPAL_RETAINED_FIELD) return false;
  if (field.field_name === INCOME_TRANSFER_FIELD) {
    if (field.is_overridden) return true;
    return (
      field.effective_value != null && Math.abs(field.effective_value) >= 0.005
    );
  }
  return true;
};

const transferAllocationLabel = (row: FundYearlyTransferAllocationDTO) => {
  const mapped = fundTypeStatementDisplayName(row.type_id);
  if (mapped) return distributionToStatementLabel(mapped);
  const custom = row.custom_name?.trim();
  if (custom) return custom;
  const dest = row.destination_fund_name?.trim();
  if (dest) return dest;
  const parts = [row.type_name?.trim(), row.subtype_name?.trim()].filter(
    Boolean
  );
  if (parts.length > 0) return parts.join(' — ');
  return row.type_id?.trim() || 'Transfer allocation';
};

const namedAwardDisplayName = (row: FundYearlyTransferAllocationDTO) => {
  const custom = row.custom_name?.trim();
  if (custom) return custom;
  const dest = row.destination_fund_name?.trim();
  if (dest) return dest;
  const typeName = row.type_name?.trim();
  if (typeName) return typeName;
  return row.type_id?.trim() || 'award';
};

const FieldOverrideActions: React.FC<{
  field: FundStatementFieldDTO;
  canManage: boolean;
  onEdit: (field: FundStatementFieldDTO) => void;
  onClear: (field: FundStatementFieldDTO) => void;
}> = ({ field, canManage, onEdit, onClear }) => {
  if (!canManage) return null;
  return (
    <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
      <IconButton
        size="small"
        aria-label={`Edit ${field.label}`}
        onClick={() => onEdit(field)}
      >
        <Edit fontSize="small" />
      </IconButton>
      {field.is_overridden && (
        <IconButton
          size="small"
          aria-label={`Clear override for ${field.label}`}
          onClick={() => onClear(field)}
        >
          <Clear fontSize="small" />
        </IconButton>
      )}
    </Box>
  );
};

const FundStatementDataTab: React.FC<FundStatementDataTabProps> = ({
  fundId,
  canManage,
}) => {
  const [selectedDate, setSelectedDate] = useState<string>('');
  const [actionError, setActionError] = useState<string | null>(null);
  const [editField, setEditField] = useState<FundStatementFieldDTO | null>(
    null
  );
  const [editValue, setEditValue] = useState('');
  const [editReason, setEditReason] = useState('');
  const [clearField, setClearField] = useState<FundStatementFieldDTO | null>(
    null
  );
  const [clearReason, setClearReason] = useState('');
  const [pdfDownloading, setPdfDownloading] = useState(false);
  const [mailingPdfDownloading, setMailingPdfDownloading] = useState(false);
  const [pdfPreviewOpen, setPdfPreviewOpen] = useState(false);
  const [pdfPreviewLoading, setPdfPreviewLoading] = useState(false);
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState<string | null>(null);
  const [principalSaving, setPrincipalSaving] = useState(false);
  const [recalcConfirmOpen, setRecalcConfirmOpen] = useState(false);
  const [recalcSaving, setRecalcSaving] = useState(false);
  const [lockConfirmOpen, setLockConfirmOpen] = useState(false);
  const [lockSaving, setLockSaving] = useState(false);

  const {
    data: datesData,
    isLoading: datesLoading,
    isError: datesIsError,
    error: datesError,
  } = useApiGet<FundStatementDatesResponse, string>(
    ['fund-statement-dates', fundId],
    getFundStatementDates,
    { enabled: !!fundId, retry: 1 },
    fundId
  );

  const datesList = datesData?.dates;

  useEffect(() => {
    const dates = datesList ?? [];
    if (!selectedDate && dates.length > 0) {
      setSelectedDate(dates[0]);
    }
    if (selectedDate && dates.length > 0 && !dates.includes(selectedDate)) {
      setSelectedDate(dates[0]);
    }
  }, [datesList, selectedDate]);

  const dates = datesList ?? [];

  const {
    data: statement,
    isLoading: statementLoading,
    isError: statementIsError,
    error: statementError,
    refetch: refetchStatement,
  } = useApiGet<FundStatementDataResponse, { fundId: string; asOf: string }>(
    ['fund-statement-data', fundId, selectedDate],
    (p) => getFundStatementData(p.fundId, p.asOf),
    {
      enabled: !!fundId && !!selectedDate,
      retry: 1,
    },
    selectedDate ? { fundId, asOf: selectedDate } : undefined
  );

  const { mutateAsync: overrideMut, isPending: saving } = useApiSend(
    (body: PostFundStatementOverrideRequest) =>
      postFundStatementOverride(fundId, body),
    () => {
      setActionError(null);
      setEditField(null);
      setClearField(null);
      setEditValue('');
      setEditReason('');
      setClearReason('');
      void refetchStatement();
    },
    (e: Error) => setActionError(getApiErrorMessage(e)),
    [
      ['fund-statement-data', fundId, selectedDate],
      ['fund-statement-dates', fundId],
    ]
  );

  const openEdit = (field: FundStatementFieldDTO) => {
    setActionError(null);
    setEditField(field);
    setEditValue(
      field.effective_value != null ? String(field.effective_value) : ''
    );
    setEditReason('');
  };

  const openClear = (field: FundStatementFieldDTO) => {
    setActionError(null);
    setClearField(field);
    setClearReason('');
  };

  const submitOverride = async () => {
    if (!editField || !selectedDate) return;
    const parsed = parseFloat(editValue);
    if (!Number.isFinite(parsed)) {
      setActionError('Enter a valid number.');
      return;
    }
    await overrideMut({
      as_of_date: selectedDate,
      field_name: editField.field_name,
      new_value: parsed,
      reason: editReason.trim() || undefined,
    });
  };

  const submitClear = async () => {
    if (!clearField || !selectedDate) return;
    await overrideMut({
      as_of_date: selectedDate,
      field_name: clearField.field_name,
      clear: true,
      reason: clearReason.trim() || undefined,
    });
  };

  const closePdfPreview = () => {
    setPdfPreviewOpen(false);
    setPdfPreviewLoading(false);
    if (pdfPreviewUrl) {
      window.URL.revokeObjectURL(pdfPreviewUrl);
      setPdfPreviewUrl(null);
    }
  };

  const handlePreviewPdf = async () => {
    if (!selectedDate) return;
    setActionError(null);
    setPdfPreviewOpen(true);
    setPdfPreviewLoading(true);
    if (pdfPreviewUrl) {
      window.URL.revokeObjectURL(pdfPreviewUrl);
      setPdfPreviewUrl(null);
    }
    try {
      const blob = await getFundStatementPdfBlob(fundId, selectedDate);
      setPdfPreviewUrl(window.URL.createObjectURL(blob));
    } catch (err) {
      setPdfPreviewOpen(false);
      setActionError(
        getApiErrorMessage(err) || 'Failed to preview statement PDF.'
      );
    } finally {
      setPdfPreviewLoading(false);
    }
  };

  const handleDownloadPdf = async () => {
    if (!selectedDate) return;
    setPdfDownloading(true);
    setActionError(null);
    try {
      await downloadFundStatementPdf(fundId, selectedDate);
    } catch (err) {
      setActionError(
        getApiErrorMessage(err) || 'Failed to download statement PDF.'
      );
    } finally {
      setPdfDownloading(false);
    }
  };

  const handleDownloadMailingPdf = async () => {
    if (!selectedDate) return;
    setMailingPdfDownloading(true);
    setActionError(null);
    try {
      await downloadFundStatementPdf(fundId, selectedDate, 'mailing');
    } catch (err) {
      setActionError(
        getApiErrorMessage(err) || 'Failed to download mailing PDF.'
      );
    } finally {
      setMailingPdfDownloading(false);
    }
  };

  const handleSavePrincipalRetained = async () => {
    if (!selectedDate) return;
    setPrincipalSaving(true);
    setActionError(null);
    try {
      await saveFundStatementPrincipalRetained(fundId, selectedDate);
      void refetchStatement();
    } catch (err) {
      setActionError(
        getApiErrorMessage(err) || 'Failed to save principal retained.'
      );
    } finally {
      setPrincipalSaving(false);
    }
  };

  const handleRecalculateDistributions = async () => {
    if (!selectedDate) return;
    setRecalcSaving(true);
    setActionError(null);
    try {
      await recalculateFundStatementDistributions(fundId, selectedDate);
      setRecalcConfirmOpen(false);
      void refetchStatement();
    } catch (err) {
      setActionError(
        getApiErrorMessage(err) || 'Failed to recalculate distributions.'
      );
    } finally {
      setRecalcSaving(false);
    }
  };

  const canGeneratePdf =
    !!statement?.category_id && statement?.calculation?.balance_ok === true;

  const isStatementLocked = statement?.is_statement_locked === true;
  const lockFileName =
    statement?.statement_lock_file_name ||
    (statement?.statement_lock_year
      ? `${statement.statement_lock_year}.pdf`
      : '');

  const handleConfirmLock = async () => {
    if (!selectedDate) return;
    setLockSaving(true);
    setActionError(null);
    try {
      await lockFundStatementPdf(fundId, selectedDate);
      setLockConfirmOpen(false);
      void refetchStatement();
    } catch (err) {
      setActionError(
        getApiErrorMessage(err) || 'Failed to lock statement PDF.'
      );
    } finally {
      setLockSaving(false);
    }
  };

  return (
    <Card sx={{ p: 2, boxShadow: 3, borderRadius: 2 }}>
      <CardHeader title="Statement Data" />
      <CardContent>
        {actionError && (
          <Box sx={{ mb: 2 }}>
            <AlertItem
              severity="error"
              description={actionError}
              onClose={() => setActionError(null)}
              keepOpen
            />
          </Box>
        )}

        {datesLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={28} />
          </Box>
        )}

        {datesIsError && (
          <AlertItem
            severity="error"
            description={
              getApiErrorMessage(datesError) ||
              'Failed to load statement dates.'
            }
            keepOpen
          />
        )}

        {!datesLoading && !datesIsError && dates.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            No information found.
          </Typography>
        )}

        {!datesLoading && dates.length > 0 && (
          <>
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                gap: 2,
                mb: 2,
              }}
            >
              <FormControl size="small" sx={{ minWidth: 220 }}>
                <InputLabel id="fund-statement-as-of-date-label">
                  As of date
                </InputLabel>
                <Select
                  labelId="fund-statement-as-of-date-label"
                  id="fund-statement-as-of-date"
                  label="As of date"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(String(e.target.value))}
                >
                  {dates.map((d) => (
                    <MenuItem key={d} value={d}>
                      {dayjs(d).format('MM/DD/YYYY')}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {statement?.category_id ? (
                <>
                  {canManage &&
                    (statement.category_id ===
                      FUND_CATEGORY_TYPE_ALLOCATION_ID ||
                      statement.category_id === FUND_CATEGORY_SCHOLARSHIP_ID ||
                      statement.category_id ===
                        FUND_CATEGORY_CHAPTER_DONOR_DESIGNATED_ID) && (
                      <Button
                        variant="outlined"
                        onClick={() => {
                          setActionError(null);
                          setRecalcConfirmOpen(true);
                        }}
                        disabled={
                          !selectedDate || recalcSaving || statementLoading
                        }
                      >
                        {recalcSaving
                          ? 'Recalculating…'
                          : 'Recalculate distributions'}
                      </Button>
                    )}
                  <Button
                    variant="outlined"
                    startIcon={<Visibility />}
                    onClick={() => void handlePreviewPdf()}
                    disabled={
                      !canGeneratePdf ||
                      !selectedDate ||
                      pdfPreviewLoading ||
                      pdfDownloading ||
                      mailingPdfDownloading ||
                      statementLoading
                    }
                  >
                    {pdfPreviewLoading ? 'Loading preview…' : 'Preview PDF'}
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<PictureAsPdf />}
                    onClick={handleDownloadPdf}
                    disabled={
                      !canGeneratePdf ||
                      !selectedDate ||
                      pdfDownloading ||
                      mailingPdfDownloading ||
                      statementLoading
                    }
                  >
                    {pdfDownloading ? 'Downloading…' : 'Download PDF'}
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<PictureAsPdf />}
                    onClick={() => void handleDownloadMailingPdf()}
                    disabled={
                      !canGeneratePdf ||
                      !selectedDate ||
                      pdfDownloading ||
                      mailingPdfDownloading ||
                      statementLoading
                    }
                  >
                    {mailingPdfDownloading
                      ? 'Downloading…'
                      : 'Download mailing PDF'}
                  </Button>
                  {canManage && (
                    <Button
                      variant="outlined"
                      startIcon={<Lock />}
                      onClick={() => setLockConfirmOpen(true)}
                      disabled={
                        !canGeneratePdf ||
                        !selectedDate ||
                        pdfDownloading ||
                        mailingPdfDownloading ||
                        statementLoading ||
                        lockSaving
                      }
                    >
                      {isStatementLocked
                        ? 'Relock statement'
                        : 'Lock statement'}
                    </Button>
                  )}
                  {isStatementLocked && lockFileName && (
                    <Typography variant="body2" color="text.secondary">
                      Locked as {lockFileName} under Annual Activity Statements.
                    </Typography>
                  )}
                  {statement.calculation &&
                    !statement.calculation.balance_ok && (
                      <Typography variant="body2" color="error">
                        Activity must balance before previewing or downloading
                        the statement PDF.
                      </Typography>
                    )}
                </>
              ) : (
                !statementLoading &&
                statement && (
                  <Typography variant="body2" color="text.secondary">
                    Set a fund category to preview or download the statement
                    PDF.
                  </Typography>
                )
              )}
            </Box>

            {statementLoading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                <CircularProgress size={28} />
              </Box>
            )}

            {statementIsError && (
              <AlertItem
                severity="error"
                description={
                  getApiErrorMessage(statementError) ||
                  'Failed to load statement data.'
                }
                keepOpen
              />
            )}

            {!statementLoading && statement && (
              <>
                <Grid container spacing={1} sx={{ mb: 2 }}>
                  {statement.previous_balance_date && (
                    <Grid item xs={12} md={6}>
                      <Typography variant="body2">
                        <b>Previous balance date:</b>{' '}
                        {dayjs(statement.previous_balance_date).format(
                          'MM/DD/YYYY'
                        )}
                      </Typography>
                    </Grid>
                  )}
                  {statement.ending_balance_date && (
                    <Grid item xs={12} md={6}>
                      <Typography variant="body2">
                        <b>Ending balance date:</b>{' '}
                        {dayjs(statement.ending_balance_date).format(
                          'MM/DD/YYYY'
                        )}
                      </Typography>
                    </Grid>
                  )}
                </Grid>

                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>MIP Account #</TableCell>
                      <TableCell>Name</TableCell>
                      <TableCell align="right">Amount</TableCell>
                      {canManage && (
                        <TableCell align="right">Actions</TableCell>
                      )}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {statement.fields
                      .filter(showMipActivityRow)
                      .map((field) => (
                        <TableRow
                          key={field.field_name}
                          sx={
                            field.field_name === 'ending_balance'
                              ? {
                                  '& td': {
                                    borderBottom: '2px solid',
                                    borderColor: 'text.primary',
                                  },
                                }
                              : undefined
                          }
                        >
                          <TableCell>{field.account_number || '—'}</TableCell>
                          <TableCell>
                            {field.label}
                            {field.is_overridden && (
                              <Chip
                                size="small"
                                label="Overridden"
                                color="warning"
                                sx={{ ml: 1 }}
                              />
                            )}
                          </TableCell>
                          <TableCell align="right">
                            {formatMoney(field.effective_value)}
                            {field.is_overridden && (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                display="block"
                              >
                                Base: {formatMoney(field.base_value)}
                              </Typography>
                            )}
                          </TableCell>
                          {canManage && (
                            <TableCell align="right">
                              <FieldOverrideActions
                                field={field}
                                canManage={canManage}
                                onEdit={openEdit}
                                onClear={openClear}
                              />
                            </TableCell>
                          )}
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>

                <Divider sx={{ mt: 0.5, mb: 2 }} />

                {statement.calculation && (
                  <Box
                    sx={{
                      mb: 2,
                      p: 2,
                      borderRadius: 1,
                      bgcolor: 'action.hover',
                    }}
                  >
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      {statement.calculation.option_label || 'Calculation'}
                    </Typography>
                    <Typography
                      variant="body2"
                      color={
                        statement.calculation.balance_ok
                          ? 'success.main'
                          : 'error.main'
                      }
                      sx={{ mb: 1 }}
                    >
                      {statement.calculation.balance_ok
                        ? `Activity check OK (computed ending ${formatMoney(statement.calculation.computed_ending)})`
                        : `Activity mismatch: computed ${formatMoney(statement.calculation.computed_ending)} (diff ${formatMoney(statement.calculation.balance_diff)})`}
                    </Typography>
                    {statement.calculation.principal_retained_is_seed && (
                      <AlertItem
                        severity="info"
                        title="Seed year"
                        description={
                          statement.calculation.custom_message ||
                          'Seed year — principal retained is loaded (not calculated). Calculation begins the following closed year.'
                        }
                        keepOpen
                      />
                    )}
                    {(statement.calculation.prior_principal_assumed ||
                      statement.calculation.prior_principal_assumed_zero) &&
                      !statement.calculation.principal_retained_is_seed && (
                        <AlertItem
                          severity="warning"
                          title="Prior principal assumed"
                          description={
                            statement.calculation.custom_message ||
                            'Could not find prior-year principal retained — assuming new fund and using fund principal amount.'
                          }
                          keepOpen
                        />
                      )}
                    {statement.calculation.distribution_minimum_blocked && (
                      <AlertItem
                        severity="warning"
                        title="Below minimum balance"
                        description={
                          statement.category_id === FUND_CATEGORY_SCHOLARSHIP_ID
                            ? `Ending balance is below the minimum fund balance of ${formatMoney(
                                statement.calculation
                                  .distribution_minimum_required ?? 0
                              )} — scholarship distributions are $0.`
                            : `Ending balance is below the minimum fund balance of ${formatMoney(
                                statement.calculation
                                  .distribution_minimum_required ?? 0
                              )} — annual income distribution is $0.`
                        }
                        keepOpen
                      />
                    )}
                    {statement.calculation.missing_prior_principal &&
                      !statement.calculation.principal_retained_is_seed && (
                        <AlertItem
                          severity="warning"
                          title="Missing prior principal"
                          description={
                            statement.calculation.custom_message ||
                            'Cannot calculate — missing prior principal retained. Backfill the prior year first.'
                          }
                          keepOpen
                        />
                      )}
                    {statement.calculation.missing_prior_principal &&
                      statement.calculation.principal_retained_is_seed && (
                        <AlertItem
                          severity="warning"
                          title="Seed principal not loaded"
                          description={
                            statement.calculation.custom_message ||
                            'Seed year — principal retained has not been loaded.'
                          }
                          keepOpen
                        />
                      )}
                    {!statement.calculation.missing_prior_principal &&
                      !statement.calculation.principal_retained_is_seed &&
                      statement.calculation.principal_retained_needs_save &&
                      statement.calculation.principal_retained != null && (
                        <Box sx={{ mb: 1 }}>
                          <AlertItem
                            severity="info"
                            title="Principal retained not saved"
                            description={`Computed principal retained for next year ${formatMoney(statement.calculation.principal_retained)} differs from stored ${formatMoney(statement.calculation.stored_principal_retained)}. Save to update the MIP row for next year’s calculation.`}
                            keepOpen
                          />
                          {canManage && (
                            <Button
                              sx={{ mt: 1 }}
                              variant="contained"
                              size="small"
                              disabled={principalSaving}
                              onClick={handleSavePrincipalRetained}
                            >
                              {principalSaving
                                ? 'Saving…'
                                : 'Save calculated principal'}
                            </Button>
                          )}
                        </Box>
                      )}
                    {statement.calculation.custom_message &&
                      statement.calculation.available_income == null &&
                      !statement.calculation.missing_prior_principal &&
                      !statement.calculation.principal_retained_is_seed && (
                        <Typography variant="body2" color="text.secondary">
                          {statement.calculation.custom_message}
                        </Typography>
                      )}
                    {statement.calculation.prior_principal_retained != null && (
                      <Typography variant="body2">
                        <b>Prior principal retained (LY):</b>{' '}
                        {formatMoney(
                          statement.calculation.prior_principal_retained
                        )}
                      </Typography>
                    )}
                    {statement.calculation.principal_retained != null && (
                      <Typography variant="body2">
                        <b>
                          {statement.calculation.principal_retained_is_seed
                            ? 'Loaded principal retained (seed):'
                            : 'Principal retained for next year:'}
                        </b>{' '}
                        {formatMoney(statement.calculation.principal_retained)}
                      </Typography>
                    )}
                    {statement.calculation.available_income != null && (
                      <Typography variant="body2">
                        <b>
                          {statement.calculation.available_income_label ||
                            'Amount available for distribution'}
                          :
                        </b>{' '}
                        {formatMoney(statement.calculation.available_income)}
                        {statement.calculation.option_code === 'option_a' &&
                          ' (ending − this year’s principal retained)'}
                      </Typography>
                    )}
                    {(statement.prior_distributions ?? []).length > 0 && (
                      <>
                        {(statement.prior_distributions ?? []).map((row) => (
                          <Typography
                            key={`${row.distributed_on}-${row.amount}`}
                            variant="body2"
                          >
                            <b>{row.label || 'Amount distributed'}:</b>{' '}
                            {formatMoney(row.amount, { subtract: true })}
                          </Typography>
                        ))}
                        {statement.final_available_for_distribution != null && (
                          <Typography variant="body2">
                            <b>Final amount available for distribution:</b>{' '}
                            {formatMoney(
                              statement.final_available_for_distribution
                            )}
                          </Typography>
                        )}
                      </>
                    )}
                    {statement.category_id === FUND_CATEGORY_SCHOLARSHIP_ID &&
                      (statement.prior_distributions ?? []).length === 0 && (
                        <>
                          <Typography variant="body2">
                            <b>
                              Already allocated
                              {statement.already_allocated_award_year
                                ? ` for ${statement.already_allocated_award_year}`
                                : ''}
                              :
                            </b>{' '}
                            {formatMoney(statement.already_allocated ?? 0, {
                              subtract: true,
                            })}
                          </Typography>
                          {statement.available_for_scholarships != null && (
                            <Typography variant="body2">
                              <b>Total available for scholarships:</b>{' '}
                              {formatMoney(
                                statement.available_for_scholarships
                              )}
                            </Typography>
                          )}
                        </>
                      )}
                    {statement.category_id ===
                      FUND_CATEGORY_CHAPTER_DONOR_DESIGNATED_ID &&
                      (statement.already_allocated ?? 0) !== 0 &&
                      (statement.prior_distributions ?? []).length === 0 && (
                        <>
                          <Typography variant="body2">
                            <b>
                              Already allocated
                              {statement.already_allocated_award_year
                                ? ` for ${statement.already_allocated_award_year}`
                                : ''}
                              :
                            </b>{' '}
                            {formatMoney(statement.already_allocated ?? 0, {
                              subtract: true,
                            })}
                          </Typography>
                          {statement.available_for_scholarships != null && (
                            <Typography variant="body2">
                              <b>Total available for scholarships:</b>{' '}
                              {formatMoney(
                                statement.available_for_scholarships
                              )}
                            </Typography>
                          )}
                        </>
                      )}
                    {statement.category_id ===
                      FUND_CATEGORY_TYPE_ALLOCATION_ID &&
                      (statement.transfer_allocations ?? []).length > 0 && (
                        <>
                          {(statement.transfer_allocations ?? []).map((row) => {
                            const isNamed = fundSubtypeRequiresNamedLimit(
                              row.subtype_id ?? ''
                            );
                            const name = namedAwardDisplayName(row);
                            if (isNamed) {
                              return (
                                <Box key={row.id || name}>
                                  <Typography variant="body2">
                                    <b>Distribution for named {name}:</b>{' '}
                                    {formatMoney(row.actual_distribution)}
                                  </Typography>
                                  <Typography variant="body2">
                                    Amount accruing for next named {name}:{' '}
                                    {formatMoney(row.accrued_distribution)}
                                  </Typography>
                                </Box>
                              );
                            }
                            return (
                              <Box key={row.id || transferAllocationLabel(row)}>
                                <Typography variant="body2">
                                  <b>{transferAllocationLabel(row)}:</b>{' '}
                                  {formatMoney(row.total_distributions)}
                                </Typography>
                                {fundSubtypeIsSpecialRecognition(
                                  row.subtype_id ?? ''
                                ) &&
                                  row.accrued_distribution > 0.005 && (
                                    <Typography
                                      variant="body2"
                                      color="text.secondary"
                                    >
                                      Leftover this year:{' '}
                                      {formatMoney(row.accrued_distribution)}
                                    </Typography>
                                  )}
                              </Box>
                            );
                          })}
                        </>
                      )}
                    {statement.fields
                      .filter(
                        (field) => field.field_name === PRINCIPAL_RETAINED_FIELD
                      )
                      .map((field) => (
                        <Box
                          key={field.field_name}
                          sx={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: 1,
                            mt: 1,
                            pt: 1,
                            borderTop: '1px solid',
                            borderColor: 'divider',
                          }}
                        >
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="body2">
                              <b>{field.label}:</b>{' '}
                              {formatMoney(field.effective_value)}
                            </Typography>
                            {field.is_overridden && (
                              <>
                                <Chip
                                  size="small"
                                  label="Overridden"
                                  color="warning"
                                  sx={{ mt: 0.5, mr: 1 }}
                                />
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  display="block"
                                >
                                  Base: {formatMoney(field.base_value)}
                                </Typography>
                              </>
                            )}
                          </Box>
                          <FieldOverrideActions
                            field={field}
                            canManage={canManage}
                            onEdit={openEdit}
                            onClear={openClear}
                          />
                        </Box>
                      ))}
                  </Box>
                )}

                <Typography variant="subtitle1" sx={{ mt: 3, mb: 1 }}>
                  Override history
                </Typography>
                {(statement.overrides?.length ?? 0) === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No overrides recorded for this date.
                  </Typography>
                ) : (
                  statement.overrides.map((row) => (
                    <Box
                      key={row.id}
                      sx={{
                        py: 1,
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                      }}
                    >
                      <Typography variant="body2">
                        <b>{row.field_label}</b>: {row.old_value || '—'} →{' '}
                        {row.new_value || '—'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {row.created_by_name || '—'}
                        {row.created_at
                          ? ` · ${dayjs(row.created_at).format('MM/DD/YYYY h:mm A')}`
                          : ''}
                        {row.reason ? ` · ${row.reason}` : ''}
                      </Typography>
                    </Box>
                  ))
                )}
              </>
            )}
          </>
        )}
      </CardContent>

      <Dialog
        open={editField !== null}
        onClose={() => setEditField(null)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Override {editField?.label}</DialogTitle>
        <DialogContent>
          <TextField
            label="Amount"
            type="number"
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            fullWidth
            size="small"
            sx={{ mt: 1, mb: 2 }}
            inputProps={{ step: '0.01' }}
          />
          <TextField
            label="Reason"
            value={editReason}
            onChange={(e) => setEditReason(e.target.value)}
            fullWidth
            size="small"
            multiline
            minRows={2}
          />
          <FormHelperText>
            Recommended. Does not change the closed-book statement row — records
            an override only.
          </FormHelperText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditField(null)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={saving || !editValue.trim()}
            onClick={() => void submitOverride()}
          >
            Save override
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={clearField !== null}
        onClose={() => setClearField(null)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Clear override</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Restore <b>{clearField?.label}</b> to the base statement value (
            {formatMoney(clearField?.base_value)})?
          </Typography>
          <TextField
            label="Reason"
            value={clearReason}
            onChange={(e) => setClearReason(e.target.value)}
            fullWidth
            size="small"
            multiline
            minRows={2}
          />
          <FormHelperText>Recommended.</FormHelperText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setClearField(null)}>Cancel</Button>
          <Button
            variant="contained"
            color="warning"
            disabled={saving}
            onClick={() => void submitClear()}
          >
            Clear override
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={recalcConfirmOpen}
        onClose={() => {
          if (!recalcSaving) setRecalcConfirmOpen(false);
        }}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Recalculate distributions</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {statement?.category_id === FUND_CATEGORY_TYPE_ALLOCATION_ID
              ? `Rebuild distribution lines from this fund's current assigned types for the selected statement year${
                  selectedDate
                    ? ` (${dayjs(selectedDate).format('MM/DD/YYYY')})`
                    : ''
                }. This may change distribution amounts and remove lines for types that are no longer assigned. For Option A funds, calculated principal retained is saved for the selected year when it differs from stored.`
              : `Rebuild stored scholarship amounts from current available income and pending allocations for the selected statement year${
                  selectedDate
                    ? ` (${dayjs(selectedDate).format('MM/DD/YYYY')})`
                    : ''
                }. This updates the yearly scholarship allocation used for this statement. For Option A funds, calculated principal retained is saved for the selected year when it differs from stored.`}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setRecalcConfirmOpen(false)}
            disabled={recalcSaving}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={recalcSaving || !selectedDate}
            onClick={() => void handleRecalculateDistributions()}
          >
            {recalcSaving ? 'Recalculating…' : 'Recalculate'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={lockConfirmOpen}
        onClose={() => {
          if (!lockSaving) setLockConfirmOpen(false);
        }}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>
          {isStatementLocked ? 'Relock statement' : 'Lock statement'}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {isStatementLocked
              ? `Replace the locked Annual Activity Statement archive${
                  lockFileName ? ` (${lockFileName})` : ''
                } with a fresh standard statement PDF for ${
                  selectedDate
                    ? dayjs(selectedDate).format('MM/DD/YYYY')
                    : 'this as-of date'
                }. The mailing layout is not archived.`
              : `Archive the standard statement PDF for ${
                  selectedDate
                    ? dayjs(selectedDate).format('MM/DD/YYYY')
                    : 'this as-of date'
                } as ${
                  lockFileName || 'the yearly PDF'
                } under Annual Activity Statements. The mailing layout is not archived.`}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setLockConfirmOpen(false)}
            disabled={lockSaving}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={lockSaving || !selectedDate || !canGeneratePdf}
            onClick={() => void handleConfirmLock()}
          >
            {lockSaving ? 'Locking…' : isStatementLocked ? 'Relock' : 'Lock'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={pdfPreviewOpen}
        onClose={closePdfPreview}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { height: '90vh' } }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <Typography component="span" variant="h6" noWrap sx={{ flex: 1 }}>
            Statement preview
            {selectedDate
              ? ` · ${dayjs(selectedDate).format('MM/DD/YYYY')}`
              : ''}
          </Typography>
          <IconButton onClick={closePdfPreview} aria-label="Close preview">
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0, height: '100%' }}>
          {pdfPreviewLoading && (
            <Box
              sx={{
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <CircularProgress />
            </Box>
          )}
          {!pdfPreviewLoading && pdfPreviewUrl && (
            <object
              data={pdfPreviewUrl}
              type="application/pdf"
              style={{ width: '100%', height: '100%', border: 'none' }}
              aria-label="Fund statement PDF preview"
            >
              <Box sx={{ p: 3 }}>
                <Typography gutterBottom>
                  Unable to display this PDF in the browser.
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<PictureAsPdf />}
                  onClick={() => void handleDownloadPdf()}
                >
                  Download instead
                </Button>
              </Box>
            </object>
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default FundStatementDataTab;
